import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { RadarValues } from './useTeamRadar';
import { loadLocalRatings } from '../demoData';

const CORNER_KEYS: Array<{ key: keyof RadarValues; matches: (id: string) => boolean }> = [
  { key: 'tech',   matches: (id) => /tech|technical/i.test(id) },
  { key: 'phys',   matches: (id) => /phys/i.test(id) },
  { key: 'psych',  matches: (id) => /psych/i.test(id) },
  { key: 'social', matches: (id) => /social/i.test(id) },
];

const ZERO: RadarValues = { tech: 0, phys: 0, psych: 0, social: 0 };

export interface PlayerRatingsResult {
  values: RadarValues;
  count: number;
  velocity: RadarValues;
}

/**
 * Mean rating per corner over the last 28 days for a single player.
 * Velocity = (last 14 days mean) − (prior 14 days mean), per corner —
 * positive = improving, negative = drifting.
 */
export function usePlayerRatings(playerId: string | undefined, refreshKey: number = 0): PlayerRatingsResult {
  const [result, setResult] = useState<PlayerRatingsResult>({ values: ZERO, count: 0, velocity: ZERO });

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    (async () => {
      try {
        const since = new Date();
        since.setDate(since.getDate() - 28);
        let data: Array<{ score: number; corner_id: string; created_at: string }> = [];
        try {
          const res = await supabase
            .from('ratings')
            .select('score, corner_id, created_at')
            .eq('ratee_id', playerId)
            .gte('created_at', since.toISOString())
            .not('revealed_at', 'is', null);
          if (cancelled) return;
          data = (res.data ?? []) as Array<{ score: number; corner_id: string; created_at: string }>;
        } catch { /* fallthrough to local */ }
        if (data.length === 0) {
          // Demo data fallback
          const sinceMs = since.getTime();
          data = loadLocalRatings()
            .filter((r) => r.ratee_id === playerId && new Date(r.created_at).getTime() >= sinceMs)
            .map((r) => ({ score: r.score, corner_id: r.corner_id, created_at: r.created_at }));
        }
        if (cancelled) return;
        if (data.length === 0) { setResult({ values: ZERO, count: 0, velocity: ZERO }); return; }

        const halfMs = 14 * 86_400_000;
        const cutoff = Date.now() - halfMs;
        const recent: Record<keyof RadarValues, { sum: number; n: number }> = {
          tech: { sum: 0, n: 0 }, phys: { sum: 0, n: 0 }, psych: { sum: 0, n: 0 }, social: { sum: 0, n: 0 },
        };
        const prior: Record<keyof RadarValues, { sum: number; n: number }> = {
          tech: { sum: 0, n: 0 }, phys: { sum: 0, n: 0 }, psych: { sum: 0, n: 0 }, social: { sum: 0, n: 0 },
        };

        for (const row of data) {
          const cornerId = String(row.corner_id ?? '');
          const score = Number(row.score ?? 0);
          const ts = new Date(String(row.created_at)).getTime();
          const match = CORNER_KEYS.find((k) => k.matches(cornerId));
          if (!match) continue;
          const bucket = ts >= cutoff ? recent : prior;
          bucket[match.key].sum += score;
          bucket[match.key].n += 1;
        }

        const avg = (b: { sum: number; n: number }) => b.n ? Math.round(b.sum / b.n) : 0;
        const values: RadarValues = {
          tech: avg(recent.tech),
          phys: avg(recent.phys),
          psych: avg(recent.psych),
          social: avg(recent.social),
        };
        const velocity: RadarValues = {
          tech: avg(recent.tech) - avg(prior.tech),
          phys: avg(recent.phys) - avg(prior.phys),
          psych: avg(recent.psych) - avg(prior.psych),
          social: avg(recent.social) - avg(prior.social),
        };
        setResult({ values, count: data.length, velocity });
      } catch (err) {
        if (!cancelled) { console.warn('[player ratings] threw', err); setResult({ values: ZERO, count: 0, velocity: ZERO }); }
      }
    })();
    return () => { cancelled = true; };
  }, [playerId, refreshKey]);

  return result;
}
